import WEBAPI from '../../services/WEBAPI';
import * as ATypes from '../../shared/actionTypes';
import { getDerivedAuthority } from '../authoritySelection/utilities';

const getPaymentDetailsLoading = (loading) => ({
  type: ATypes.GET_PAYMENT_DETAILS_LOADING,
  payload: loading,
});

const getPaymentDetailsSuccess = (paymentDetails) => ({
  type: ATypes.GET_PAYMENT_DETAILS_SUCCESS,
  payload: paymentDetails,
});

const getPaymentDetailsError = (error) => ({
  type: ATypes.GET_PAYMENT_DETAILS_ERROR,
  payload: error,
});

export const getPaymentDetails = (code) => {
  return async (dispatch, getState) => {
    const store = getState();
    const authority = getDerivedAuthority(store);

    dispatch(getPaymentDetailsLoading(true));

    if (!authority) {
      dispatch(getPaymentDetailsLoading(false));
      return Promise.reject(new Error('Authority not found'));
    }

    if (!code) {
      dispatch(getPaymentDetailsLoading(false));
      return Promise.reject(new Error('Code not found'));
    }

    try {
      const res = await WEBAPI.getPaymentDetailsRequest(authority, code);
      dispatch(getPaymentDetailsSuccess(res.result || {}));
      return res.result || {};
    } catch (error) {
      console.log('Failed to get payment details:', error);
      dispatch(getPaymentDetailsError(error));
      return Promise.reject(error);
    } finally {
      dispatch(getPaymentDetailsLoading(false));
    }
  };
};

export const getPublicPaymentDetails = (code, recaptchaToken) => {
  return async (dispatch, getState) => {
    const { deeplinkReducer: deeplinkState } = getState();
    const authority = deeplinkState?.authority;

    dispatch(getPaymentDetailsLoading(true));

    if (!code) {
      dispatch(getPaymentDetailsLoading(false));
      return Promise.reject(new Error('Code not found'));
    }

    if (!recaptchaToken) {
      dispatch(getPaymentDetailsLoading(false));
      return Promise.reject(new Error('Captcha not found'));
    }

    if (!authority) {
      dispatch(getPaymentDetailsLoading(false));
      return Promise.reject(new Error('Authority not found'));
    }

    try {
      const res = await WEBAPI.getPublicPaymentDetailsRequest(
        authority,
        code,
        recaptchaToken
      );
      dispatch(getPaymentDetailsSuccess(res.result || {}));
      return res.result || {};
    } catch (error) {
      console.log('Failed to get public payment details:', error);
      dispatch(getPaymentDetailsError(error));
      return Promise.reject(error);
    } finally {
      dispatch(getPaymentDetailsLoading(false));
    }
  };
};

export const preparePaymentLoading = (loading) => ({
  type: ATypes.PREPARE_PAYMENT_LOADING,
  payload: loading,
});

export const makePaymentLoading = (loading) => ({
  type: ATypes.MAKE_PAYMENT_LOADING,
  payload: loading,
});

export const makePaymentSuccess = (paymentData) => ({
  type: ATypes.MAKE_PAYMENT_SUCCESS,
  payload: paymentData,
});

export const makePaymentError = (error) => ({
  type: ATypes.MAKE_PAYMENT_ERROR,
  payload: error,
});

export const makePayment = (paymentId, options = {}) => {
  return async (dispatch, getState) => {
    const store = getState();
    const authority = options.isPublic
      ? store?.deeplinkReducer?.authority
      : getDerivedAuthority(store);

    if (!authority) {
      return Promise.reject(new Error('Authority not found'));
    }

    try {
      dispatch(makePaymentLoading(true));
      const res = options.isPublic
        ? await WEBAPI.makePublicPaymentWithoutCard(authority, paymentId)
        : await WEBAPI.makePaymentWithoutCard(authority, paymentId, {
            skipAuth: false,
          });

      const publicRedirectUri =
        typeof res?.redirectUrl === 'string' && res.redirectUrl.length > 0
          ? res.redirectUrl
          : null;

      const htmlPayload =
        typeof res?.html === 'string' && res.html.length > 0
          ? `data:text/html;charset=utf-8,${encodeURIComponent(res.html)}`
          : null;

      const authorizationUrl =
        res?.result?.auth?.authorization_url || publicRedirectUri || htmlPayload || '';

      dispatch(makePaymentSuccess(authorizationUrl));

      return authorizationUrl;
    } catch (error) {
      dispatch(makePaymentError(error));
      return Promise.reject(error);
    } finally {
      dispatch(makePaymentLoading(false));
    }
  };
};

export const preparePaymentSuccess = (paymentData) => ({
  type: ATypes.PREPARE_PAYMENT_SUCCESS,
  payload: paymentData,
});

export const preparePaymentError = (error) => ({
  type: ATypes.PREPARE_PAYMENT_ERROR,
  payload: error,
});

export const preparePayment = (data) => {
  return async (dispatch, getState) => {

    console.log('Dispatching preparePayment with data:', data);

    const store = getState();
    const authority = data?.isPublic
      ? store?.deeplinkReducer?.authority
      : getDerivedAuthority(store);

    console.log('Preparing payment with authority:', authority);

    if (!authority) {
      return Promise.reject(new Error('Authority not found'));
    }

    try {
      
      dispatch(preparePaymentLoading(true));

      console.log('Preparing payment with data:', data);
      const { isPublic, ...payload } = data || {};

      const res = isPublic
        ? await WEBAPI.preparePublicPaymentRequest(authority, payload)
        : await WEBAPI.preparePaymentRequest(authority, payload);

      await dispatch(preparePaymentSuccess(res.result || {}));

      const paymentId =
        res.result?.payment_id ||
        res.result?.payment?.id ||
        data.payment_id;

      const isAlreadySettled =
        res.result?.paid === 1 ||
        !paymentId ||
        res.result?.amount_in_cents === 0;

      if (isAlreadySettled) {
        return null;
      }

      if (isPublic) {
        return { paymentId };
      }

      return await dispatch(makePayment(paymentId, { isPublic }));
    } catch (error) {
      dispatch(preparePaymentError(error));
      return Promise.reject(error);
    } finally {
      dispatch(preparePaymentLoading(false));
    }
  };
};
